import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AlfredClientService } from '../alfred-client.service';
import { Answer } from '../models/answer/answer.model';
import { GaveAnswer } from '../models/on-interaction/gave-answer.model';
import { OnInteraction } from '../models/on-interaction/on-interaction.model';
import { QuestionStep } from '../models/step/question-step.model';
import { Step } from '../models/step/step.model';
import { ThankYouType } from '../shared/enums/thank-you-type.enum';
import { QuestionAnswer } from './question/question-control/question-type.interface';
import { AnswerMapper } from '../answer-mapper';

@Injectable()
export class StepSupplierService {
  private readonly alfredClientService: AlfredClientService =
    inject(AlfredClientService);

  /**
   * Subject containing the id of the current session.
   */
  private readonly sessionIdSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>(undefined as never);

  /**
   * Set the session ID.
   */
  public setSessionId(sessionId: string): void {
    this.sessionIdSubject.next(sessionId);
  }

  /**
   * Get the session ID.
   */
  public getSessionId(): string {
    return this.sessionIdSubject.getValue();
  }

  /**
   * Navigate to the previous question.
   */
  public onPrevClicked(
    step: Step | undefined,
    newAnswer: QuestionAnswer | undefined,
  ): void {
    if (step instanceof QuestionStep) {
      this.alfredClientService.previous(
        new GaveAnswer(
          step.question,
          this.alfredClientService.isApiMode()
            ? this.createAnswerInstance(step, newAnswer)
            : undefined,
        ),
      );
      return;
    }

    this.alfredClientService.previous();
  }

  /**
   * Post the answer and navigate to the next question.
   */
  public onNextAnswer(
    step: Step | undefined,
    newAnswer: QuestionAnswer | undefined,
  ): void {
    if (step instanceof QuestionStep) {
      this.alfredClientService.next(
        new GaveAnswer(
          step.question,
          this.alfredClientService.isApiMode()
            ? this.createAnswerInstance(step, newAnswer)
            : undefined,
        ),
      );
      return;
    }

    this.alfredClientService.next();
  }

  /**
   * Navigate to the next question.
   */
  public onNextClick(step: Step, interaction: OnInteraction | undefined): void {
    if (
      step.thankYouType === ThankYouType.RETURN &&
      step.position === step.totalQuestions
    ) {
      this.alfredClientService.finish();
      // return; // HK - Do not return so that the self-check answer can be patched
    }
    this.alfredClientService.next(interaction);
  }

  private createAnswerInstance(
    step: QuestionStep,
    newAnswer: QuestionAnswer | undefined | null,
  ): Answer | undefined {
    if (newAnswer === null || newAnswer === undefined) {
      return undefined;
    }

    return AnswerMapper.fromDto({
      id: step.answer?.id,
      type: step.answer?.type,
      answer: newAnswer,
    });
  }
}
